import { Component, OnInit, DoCheck, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { first } from 'rxjs/operators';
import {
  AlertService,
  AuthenticationService,
  GlobalService,
  ShadowService,
} from 'src/app/shared/_services';
import { environment } from 'src/environments/environment';
import { ModalController, Platform } from '@ionic/angular';
import { TermsPage } from 'src/app/shared/modals/terms/terms.page';
import { Location } from '@angular/common';
import { LoginStore } from 'src/app/shared/_services/store';

@Component({
  selector: 'app-login',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
})
export class LoginPage implements OnInit, OnDestroy, DoCheck {
  checked: boolean;
  showIcon = true;
  disabled = true;
  loading = false;
  loginForm: FormGroup;
  returnUrl: string;
  error: string;
  success: string;
  subscription: any;
  focusedElement: any;
  inputType = 'password';
  banks = [];
  ussdBanks = [];
  filteredBanks: any[];

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthenticationService,
    private alertService: AlertService,
    private modalController: ModalController,
    private platform: Platform,
    private location: Location,
    private shadowService: ShadowService,
    private globalService: GlobalService,
    private loginStore: LoginStore
  ) {
    // redirect to home if already logged in
    if (this.auth.currentUserValue) {
      this.router.navigate(['/dashboard']);
    }
  }

  get email() {
    return this.loginForm.get('email');
  }

  get password() {
    return this.loginForm.get('password');
  }

  public errorMessages = {
    email: [
      { type: 'required', message: 'Email is required' },
      { type: 'pattern', message: 'Please enter a valid email' },
    ],
    password: [
      { type: 'required', message: 'Password is required' },
      { type: 'minlength', message: 'Password is too short' },
      {
        type: 'pattern',
        message: 'Are you sure that\'s your correct Password? 😬',
      },
    ],
  };

  togglePassword() {
    this.showIcon = !this.showIcon;
    this.inputType === 'password'
      ? (this.inputType = 'text')
      : (this.inputType = 'password');
  }

  toggleRemember() {
    this.checked === false ? (this.checked = true) : (this.checked = false);
    this.loginStore.setIsRememberMeChecked(this.checked);
  }

  toggleShake() {
    if (this.platform.is('cordova')) {
      const shake = document.getElementById('try-demo');
      shake.classList.remove('shake');
      shake.classList.contains('mr--1')
        ? shake.classList.remove('mr--1')
        : shake.classList.add('mr--1');
      if (!this.loginStore.getShake()) {
        this.loginStore.setShake(true);
      }
    }
  }

  clearError() {
    this.error = null;
  }

  signUp() {
    this.error = null;
    this.router.navigateByUrl('/signup');
  }

  verify() {
    this.error = null;
    this.router.navigateByUrl('/verify-email');
  }

  forgotPassword() {
    this.error = null;
    this.router.navigateByUrl('/forgot-password');
  }

  login() {
    this.loading = true;
    this.disabled = true;
    this.inputType = 'password';
    this.showIcon = true;
    this.error = null;
    this.success = null;

    // stop here if form is invalid
    if (this.loginForm.invalid) {
      this.loading = false;
      return;
    }

    this.auth
      .login(this.email.value, this.password.value)
      .pipe(first())
      .subscribe(
        (data) => {
          // this.location.replaceState('/dashboard');
          if (this.loginStore.getIsRememberMeChecked() === 'false') {
            this.password.setValue('');
          }
          this.loginStore.setIsLoggedIn(true);
          this.router.navigate([this.returnUrl]);
          this.loading = false;
          console.log(data);
          // document.querySelector('app-signup').remove();
        },
        (error) => {
          window.stop();
          this.error = error.error.message;
          this.loading = false;
          console.log(error);
          if (this.error) {
            if (error.error.message.includes('not verified')) {
              this.error = null;
              this.router.navigateByUrl('/verify-email');
              // this.alertService.callToAction('Oops!', 'Looks like you are not verified yet', 'Verify Email');
            } else if (
              error.error.message.includes('connect ETIMEDOUT') ||
              !error.error.message.includes('incorrect')
            ) {
              this.alertService.loadingNullDuration(
                'Connection timed out. Please check your internet connection and try again.' +
                  this.globalService.errorText.persists
              );
            }
          } else if (error.statusText === 'Unknown Error') {
            this.alertService.loadingNullDuration(
              'Connection timed out. Please check your internet connection and try again.' +
                this.globalService.errorText.persists
            );
          } else {
            this.alertService.loadingNullDuration(
              'Please check your details and try again'
            );
          }
        }
      );
    if (this.success) {
      this.router.navigate([this.returnUrl]);
    }
  }

  async terms() {
    const modal = await this.modalController.create({
      component: TermsPage,
      cssClass: 'bottom-modal',
      backdropDismiss: true,
      swipeToClose: true,
    });
    return await modal.present();
  }

  tryDemo() {
    this.router.navigate(['/demo/payment'], { queryParams: { demo: true } });
  }

  goHome() {
    if (!this.platform.is('cordova')) {
      this.router.navigateByUrl('/home');
    } else {
      return;
    }
  }

  ionViewDidEnter() {
    const shake = document.getElementById('try-demo');
    this.loginStore.getShake() === 'true'
      ? shake.classList.remove('shake')
      : shake.classList.add('shake');
    this.loginStore.setDemoAccountName('');
    this.subscription = this.platform.backButton.subscribeWithPriority(
      99999,
      () => {
        if (
          this.constructor.name === 'LoginPage' &&
          (this.router.url === '/' || this.router.url === '/login')
        ) {
          this.alertService.checkForAlert();
        } else {
          this.location.back();
        }
      }
    );
  }

  ionViewWillEnter() {
    document.title = 'Login | Bank3D';
    this.shadowService.button();
  }

  ionViewDidLeave() {
    this.subscription.unsubscribe();
  }

  ngOnInit() {
    this.loginStore.getIsRememberMeChecked() === 'true'
      ? (this.checked = true)
      : (this.checked = false);
    this.loginForm = this.formBuilder.group({
      email: [
        this.loginStore.getLoginEmail(),
        [
          Validators.required,
          Validators.pattern('^[\\w-\\.]+@([\\w-]+\\.)+[\\w-]{2,4}$'),
        ],
      ],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(
            '((?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[\\W]).{8,})'
          ),
        ],
      ],
    });
    // get return url from route parameters or default to '/dashboard'
    const returnUrl = 'returnUrl';
    this.returnUrl = this.route.snapshot.queryParams[returnUrl] || '/dashboard';

    this.globalService.getBanks();
    console.log(environment.message, environment.production);
    window.addEventListener('keydown', (e) => {
      const pathname = window.location.pathname;
      if (
        e.key === 'Enter' &&
        this.disabled === false &&
        pathname.includes('login')
      ) {
        this.login();
      }
    });
  }

  ngDoCheck(): void {
    this.loginStore.setLoginEmail(this.email.value);
    this.loginStore.removeIsSignupPage();
    if (this.loginStore.getLoginEmail() === 'null') {
      this.loginStore.setLoginEmail('');
    }
    if (!this.loginForm.valid) {
      this.disabled = true;
    } else if (this.loading === true) {
      this.disabled = true;
    } else {
      this.disabled = false;
    }
  }

  ngOnDestroy(): void {}
}
